// src/layout/Sidebar.jsx
// 左サイドバー。ロール(+機能フラグ) に基づくメニューを config/menu から取得して描画。

import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { getMenuItems } from '../config/menu';
import Icon from '../components/Icon';

export default function Sidebar({ user, open = false, onClose = () => {} }) {
  const items    = getMenuItems(user);
  const location = useLocation();

  // サブメニューは常に出す【2026-09-07】
  //   機能が存在すること自体を見せたいので、授業を開いていなくても並べる。
  //   行き先は「今開いている授業 → 直前に開いた授業 → 授業一覧」の順に決める。
  //   授業一覧へ送る場合は ?section= を付けて、選んだ授業のそのページに直行させる。
  const classMatch  = location.pathname.match(/^\/classes\/(\d+|all)/);
  const openClassId = classMatch ? classMatch[1] : null;

  // サブメニューの開閉。閉じておけば次に開いたときも閉じたまま【2026-09-07】
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('sidebarCollapsed') || '{}'); }
    catch (e) { return {}; }
  });

  const toggleMenu = (key) => {
    setCollapsed(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('sidebarCollapsed', JSON.stringify(next)); }
      catch (e) { /* 保存できなくても動く */ }
      return next;
    });
  };

  let lastClassId = null;
  try { lastClassId = localStorage.getItem('lastClassId'); } catch (e) { /* 参照できなくても動く */ }

  const targetClassId = openClassId || lastClassId;

  // いまどのサブページを見ているか【2026-09-07】
  //   授業を開いていないときのリンクは /classes?section=... で pathname が同じになり、
  //   NavLink だと全部「選択中」になってしまうので自前で判定する。
  //   分かれていた頃のURL（export / import）は出席記録扱いにする
  const LEGACY = { export: 'summary', import: 'summary' };
  const rawSection = openClassId
    ? (location.pathname.split('/')[3] || 'summary')
    : (location.pathname === '/classes'
        ? new URLSearchParams(location.search).get('section')
        : null);
  const currentSection = LEGACY[rawSection] || rawSection;

  const subItemsFor = (it) => {
    if (!it.children || it.to !== '/classes') return null;
    // 「すべての授業」を開いていてもサブメニューは全部出す【2026-09-07 修正】
    //   授業スケジュールと受講者名簿は個別の授業でしか意味を持たないので、
    //   消すのではなく行き先だけ「直前に開いた授業」に振り替える。
    const perClassId = (openClassId && openClassId !== 'all') ? openClassId : lastClassId;
    return it.children.map(ch => {
      const id = ch.section === 'summary' ? targetClassId : perClassId;
      return {
        key: ch.section,
        to: id ? '/classes/' + id + '/' + ch.section : '/classes?section=' + ch.section,
        label: ch.label,
      };
    });
  };
  return (
    <aside className={'sidebar' + (open ? ' drawer-open' : '')}>
      <div className="sidebar-header">
        <div className="sidebar-logo"><Icon name="grad-cap" size={30} /></div>
        <div>
          <h2 className="sidebar-title">出席管理</h2>
          <p className="sidebar-subtitle">Attendance System</p>
        </div>
      </div>
      <nav className="sidebar-nav">
        {items.map(it => {
          const subs = subItemsFor(it);
          const isOpen = subs && !collapsed[it.to];
          return (
            <React.Fragment key={it.to}>
              <div className="sidebar-nav-row">
                <NavLink
                  to={it.to}
                  end={it.to === '/'}
                  onClick={onClose}
                  className={({ isActive }) =>
                    'sidebar-nav-item' +
                    // サブメニューは常に出すので、親のハイライトはパスで判定する
                    ((isActive || location.pathname.startsWith(it.to + '/')) ? ' active' : '')}
                >
                  <span className="sidebar-nav-icon"><Icon name={it.icon} /></span>
                  <span className="sidebar-nav-label">{it.label}</span>
                </NavLink>
                {subs && (
                  <button
                    type="button"
                    className="sidebar-nav-toggle"
                    onClick={() => toggleMenu(it.to)}
                    aria-expanded={isOpen}
                    aria-label={(isOpen ? '閉じる: ' : '開く: ') + it.label}
                    title={isOpen ? '閉じる' : '開く'}
                  >
                    <span className={'sidebar-nav-caret' + (isOpen ? '' : ' collapsed')}>⌃</span>
                  </button>
                )}
              </div>

              {isOpen && (
                <div className="sidebar-subnav">
                  {subs.map(sub => (
                    <Link
                      key={sub.key}
                      to={sub.to}
                      onClick={onClose}
                      className={'sidebar-subnav-item' +
                        (sub.key === currentSection ? ' active' : '')}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </React.Fragment>
          );
        })}
      </nav>
      <div className="sidebar-footer">
        {/* 利用規約・プライバシーポリシーへの導線【2026-06-05 追加】 */}
        <small className="sidebar-legal">
          <a href="/terms"   target="_blank" rel="noopener noreferrer">利用規約</a>
          <span className="sidebar-legal-sep">・</span>
          <a href="/privacy" target="_blank" rel="noopener noreferrer">プライバシーポリシー</a>
        </small>
        <small>v3.0.0</small>
      </div>
    </aside>
  );
}
